import { MemberTaskFeedItem, MemberTaskFeedItemTypes, Task, TaskGroupSummary, TaskLocation } from 'modules/Tasks/types';
import collectiveApi from 'modules/common/collectiveApi';

type FeedItemType = (typeof MemberTaskFeedItemTypes)[keyof typeof MemberTaskFeedItemTypes];

/**
 * `dashboard` returns all of a member's ranked tasks; `location` scopes to one page.
 * `limit` defaults to 5.
 */
export type GetMemberPriorityTasksParams =
    | { surface: 'dashboard'; limit?: number }
    | { surface: 'location'; location: TaskLocation; limit?: number };

export interface MemberPriorityTasksResult {
    items: MemberTaskFeedItem[];
    totalItems: number; // pre-limit total
}

/**
 * One ranked feed entry as the server sends it. Exactly one of `task`/`group` is set, matching
 * `item_type`. Entries arrive already in rank order, so we keep the array order as-is; the per-item
 * ranking fields aren't read — the signal the cards need already rides on `task.priority`/`group.priority`.
 */
interface MemberPriorityFeedServerItem {
    item_type: FeedItemType;
    task: Task | null;
    group: TaskGroupSummary | null;
}

/** Unwrap a server feed entry into the FE feed-item union; skip malformed entries defensively. */
function toFeedItem(item: MemberPriorityFeedServerItem): MemberTaskFeedItem | null {
    if (item.item_type === MemberTaskFeedItemTypes.TASK && item.task) {
        return { item_type: MemberTaskFeedItemTypes.TASK, task: item.task };
    }
    if (item.item_type === MemberTaskFeedItemTypes.GROUP && item.group) {
        return { item_type: MemberTaskFeedItemTypes.GROUP, group: item.group };
    }
    return null;
}

export default async function getMemberPriorityTasks(
    params: GetMemberPriorityTasksParams
): Promise<MemberPriorityTasksResult> {
    const { surface, limit = 5 } = params;
    const { data } = await collectiveApi.get<{ total_items: number; items: MemberPriorityFeedServerItem[] }>(
        '/v2/members/tasks/feed/',
        { params: { surface, limit, ...(params.surface === 'location' && { location: params.location }) } }
    );
    return {
        items: data.items.map(toFeedItem).filter((item): item is MemberTaskFeedItem => item !== null),
        totalItems: data.total_items,
    };
}
